import { type FC } from "react";
import {
  AutocompleteInput,
  BooleanInput,
  email,
  FormTab,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  TabbedForm,
  TextInput,
  Toolbar,
  useRecordContext,
  FormDataConsumer,
} from "react-admin";
import { Grid } from "@mui/material";
import { currencyChoices } from "@/constants/currencies";
import { type TSupplier } from "@/api";
import { availableSeller } from "../shared/available-user";
import { availableAuthEmail } from "../shared/available-auth-email";
import { phonePrefixChoices } from "@/config/country";
import { OptimizedImageInput } from "@/components/elements/OptimizedImageInput";
import { SupplierFeaturesInput } from "@/components/suppliers/SupplierFeaturesInput";

const SupplierEditToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

export const SupplierForm: FC<{ isEdit?: boolean }> = ({ isEdit }) => {
  const supplier = useRecordContext<TSupplier>();

  return (
    <TabbedForm toolbar={<SupplierEditToolbar />} warnWhenUnsavedChanges>
      <FormTab label="Principal">
        <Grid container spacing={4}>
          {isEdit && (
            <Grid item xs={4}>
              <TextInput source="id" disabled fullWidth />
            </Grid>
          )}
          <Grid item xs={4}>
            <TextInput source="name" validate={required()} fullWidth />
          </Grid>

          <Grid item xs={4}>
            <TextInput source="publicName" fullWidth />
          </Grid>

          <Grid item xs={4}>
            <TextInput
              source="email"
              validate={[
                required(),
                email(),
                availableAuthEmail(supplier?.email),
                availableSeller("email", supplier?.email),
              ]}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              source="communicationChannel"
              validate={[email()]}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="contactName" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <TextInput source="socialReason" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <SelectInput
              source="phonePrefix"
              choices={phonePrefixChoices}
              disabled={isEdit}
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <TextInput
              source="phone"
              validate={[number("Solo números")]}
              inputMode="numeric"
              fullWidth
            />
          </Grid>
          <Grid item xs={4}>
            <AutocompleteInput
              source="currency"
              label="Moneda"
              choices={[...currencyChoices]}
              optionText="name"
              optionValue="id"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <OptimizedImageInput
              source="logo"
              width={200}
              height={200}
              format={(v) => (typeof v === "string" ? { src: v } : v)}
            />
          </Grid>

          <TextInput
            source="source"
            defaultValue={"react_backoffice"}
            type="hidden"
            sx={{ display: "none" }}
          />
          <Grid item xs={4}>
            <BooleanInput source="status" defaultValue={true} />
          </Grid>
          <Grid item xs={4}>
            <BooleanInput
              source="showCurves"
              label="Habilitar la opcion de curvas en los pedidos"
              defaultValue={false}
            />
          </Grid>
          <Grid item xs={4}>
            <BooleanInput
              source="showBrandsBannerInCatalog"
              label="Mostrar banner de marcas en catálogo"
              defaultValue={false}
            />
          </Grid>
          <Grid item xs={4}>
            <BooleanInput
              source="taniaEnabled"
              label="Habilitar tania"
              defaultValue={false}
            />
          </Grid>
          <FormDataConsumer>
            {({ formData }) =>
              formData?.taniaEnabled ? (
                <Grid item xs={4}>
                  <BooleanInput
                    source="aiStartsConversation"
                    label="IA en conversaciones"
                    defaultValue={false}
                  />
                </Grid>
              ) : null
            }
          </FormDataConsumer>

          <Grid item xs={4}>
            <BooleanInput source="notificationOrderCreate" />
          </Grid>

        </Grid>
      </FormTab>
      <FormTab label="Características">
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <SupplierFeaturesInput source="features" />
          </Grid>
        </Grid>
      </FormTab>
    </TabbedForm>
  );
};
